// @ts-nocheck
import {useEffect, useState} from "react";

import {useApi, usePlayMakersConfig} from "../PlayMakersProvider";
import {useSchema} from "./useSchema";

// Dynamically load barista and hook it up to the API
export const useBarista = () => {
  const {BARISTA_BASEURL, BARISTA_VERSION} = usePlayMakersConfig();
  const {baristaRequest} = useApi();
  const [baristaLoaded, setBaristaLoaded] = useState<boolean>(false);
  const [baristaLoadingError, setBaristaLoadingError] = useState<boolean>(false);
  const [BARISTA, setBARISTA] = useState(undefined);

  // We dynamically load barista's external script depending on config
  // `baristaLoaded` is a state to wait for the end of the loading
  useEffect(() => {
    const BARISTA_BUNDLE_URL: string = `${BARISTA_BASEURL}/${BARISTA_VERSION}/barista_bundle.js`;
    setBaristaLoaded(false);
    setBaristaLoadingError(false);
    const handleScript = ({type}: any) => {
      if(type === "load") {
        window.BARISTA.setApiBaristaRequest(baristaRequest);
        setBARISTA(window.BARISTA);
        setBaristaLoaded(true);
      } else
        setBaristaLoadingError(true);
    };

    let script = document.querySelector(`script[src="${BARISTA_BUNDLE_URL}"]`);
    if(!script) {
      script = document.createElement("script");
      script.type = "application/javascript";
      script.src = BARISTA_BUNDLE_URL;
      script.async = true;
      document.body.appendChild(script);
    } else if(window.BARISTA) {
      setBARISTA(window.BARISTA);
      setBaristaLoaded(true);
    }

    script.addEventListener("load", handleScript);
    script.addEventListener("error", handleScript);

    return () => {
      script.removeEventListener("load", handleScript);
      script.removeEventListener("error", handleScript);
    };
  }, [BARISTA_BASEURL, BARISTA_VERSION]);

  return {
    BARISTA,
    baristaLoaded,
    baristaLoadingError
  };

};


// Hook specialized in handling submission object
export const useBaristaSubmission = (schemaId: string, submissionData: any, setSubmissionData: any) => {
  const {schema} = useSchema(schemaId);
  const {BARISTA, baristaLoaded} = useBarista();

  // States ok: ready for commit, error: submission not valid, pending: computing rules
  // ready: ready for compute rules, actionRequired: user action required (select proposal)
  const [submissionStatus, _setSubmissionStatus] = useState<"ok"|"error"|"pending"|"ready"|"actionRequired">("ready");
  const [submissionController, setSubmissionController] = useState<any>(null);
  const [correctedRules, setCorrectedRules] = useState<any>(null);
  const [baristaSchema, setBaristaSchema] = useState<any>(null);
  const [baristaSubmission, _setBaristaSubmission] = useState<any>(null);

  useEffect(() => {
    if(!baristaLoaded || !schema) return;
    loadSchema();
  }, [schema, baristaLoaded]);

  // Wrappers for the update of the barista Submission object, the JSON output and a local "Controller"
  const setBaristaSubmission = (sub: any) => {
    _setBaristaSubmission(sub);
    setSubmissionController(sub.toFrontEnd());
    setSubmissionData(sub.toString());
  };
  const updateSubmissionData = () => {
    setSubmissionController(baristaSubmission.toFrontEnd());
    setSubmissionData(baristaSubmission.toString());
  };

  async function loadSchema() {
    const sch = await BARISTA.Schema.parse(schema.data);
    let sub;
    setBaristaSchema(sch);
    if(submissionData) {
      sub = await BARISTA.Submission.parse(submissionData);
      await sub.assignSchema(sch, "forceChildren");
    } else
      sub = await new BARISTA.Submission(sch);
    setBaristaSchema(sch);
    setBaristaSubmission(sub);
  }

  if(!baristaLoaded) return {};

  const getFileData = file => new Promise(resolve => {
    const reader = new FileReader;
    reader.onload = e => resolve(e.target.result);
    reader.readAsDataURL(file);
  });

  function getElement(elementId) {
    const element = submissionController.find(e => e.id === elementId);
    return element;
  }

  function getKey(elementId, keyId) {
    const element = submissionController.find(e => e.id === elementId);
    const key = element.keys.find(k => k.id === keyId);
    return key;
  }

  function getHasProposals() {
    return submissionController.length > 0 &&
      submissionController[0].hasProposals();
  }

  async function setValue(elementId, keyId, value) {
    if(value instanceof File) value = await getFileData(value);
    const key = getKey(elementId, keyId);
    key.setValue(value);
    updateSubmissionData();
  }

  function setSubmissionStatus(status) {
    _setSubmissionStatus(getHasProposals() ? "actionRequired" : status);
  }

  async function acceptProposal(elementId, n) {
    const element = getElement(elementId);
    element.acceptProposal(n);
    setSubmissionStatus("ready");
    updateSubmissionData();
  }

  async function computeRules() {
    _setSubmissionStatus("pending");
    try {
      const failedRules = await baristaSubmission.computeRules(true);
      updateSubmissionData();
      if(failedRules.length === 0) return setSubmissionStatus("ok");
      const {corrected, notCorrected, withProposals} = await baristaSubmission.correctRules(2);
      setCorrectedRules({corrected, notCorrected, withProposals});
      updateSubmissionData();
      setSubmissionStatus(notCorrected.length === 0 && withProposals.length === 0 ? "ok" : "ready" );
    } catch (error) {
      _setSubmissionStatus("error");
    }
  }

  async function processZip(file) {
    const sub = await BARISTA.Submission.reconstructFromZip(file, baristaSchema, true);
    setBaristaSubmission(sub);
  }

  async function downloadAsZip() {
    const blob = await baristaSubmission.convertToZip();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "submission.zip";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  return {
    acceptProposal,
    baristaAcceptProposal: acceptProposal,
    baristaComputeRules: computeRules,
    baristaCorrectedRules: correctedRules,
    baristaDownloadAsZip: downloadAsZip,
    baristaProcessZip: processZip,
    baristaSchema,
    baristaSetSubmissionStatus: setSubmissionStatus,
    baristaSetValue: setValue,
    baristaSubmission,
    baristaSubmissionController: submissionController,
    baristaSubmissionStatus: submissionStatus,
    computeRules,
    correctedRules,
    downloadAsZip,
    processZip,
    setSubmissionStatus,
    setValue,
    submissionController,
    submissionStatus
  };
};
